// Scanner helpers for `@if` condition preprocessing before tokenization.
// Keeps top-level logical/comparison detection separate from operand rewriting.
import { findMatchingPair } from './scanner-utils'

export type ComparisonOperator = '===' | '!==' | '>=' | '<=' | '>' | '<'
export type LogicalOperator = '&&' | '||'

// Split condition source by top-level logical operators while preserving nested groups.
export function splitLogicalOperators(source: string): { segments: string[]; operators: LogicalOperator[] } {
  const segments: string[] = []
  const operators: LogicalOperator[] = []
  let cursor = 0

  // Scan once and split only when `&&` or `||` appears at top-level depth.
  for (let index = 0, depth = 0, quote: "'" | '"' | null = null; index < source.length; index += 1) {
    const char = source[index]
    const previous = index > 0 ? source[index - 1] : ''
    if (quote) {
      if (char === quote && previous !== '\\') quote = null
      continue
    }
    if (char === "'" || char === '"') {
      quote = char
      continue
    }
    if (char === '(') depth += 1
    else if (char === ')' && depth > 0) depth -= 1
    const operator = depth === 0 ? readLogicalOperatorAt(source, index) : null
    if (!operator) continue
    segments.push(source.slice(cursor, index))
    operators.push(operator)
    index += operator.length - 1
    cursor = index + 1
  }

  segments.push(source.slice(cursor))
  // Return split logical segments and their operators.
  return { segments, operators }
}

// Find the first comparison operator that appears outside nested parentheses.
export function findTopLevelComparison(source: string): { operator: ComparisonOperator; start: number; end: number } | null {
  // Scan source until one top-level comparison operator is found.
  for (let index = 0, depth = 0, quote: "'" | '"' | null = null; index < source.length; index += 1) {
    const char = source[index]
    const previous = index > 0 ? source[index - 1] : ''
    if (quote) {
      if (char === quote && previous !== '\\') quote = null
      continue
    }
    if (char === "'" || char === '"') {
      quote = char
      continue
    }
    if (char === '(') depth += 1
    else if (char === ')' && depth > 0) depth -= 1
    const operator = depth === 0 ? readComparisonOperatorAt(source, index) : null
    if (operator) return { operator, start: index, end: index + operator.length }
  }

  // Return null when the segment has no top-level comparison operator.
  return null
}

// Check whether the source is fully wrapped by one outer parenthesis pair.
export function unwrapWrappedParentheses(source: string): string | null {
  return source.startsWith('(') && findMatchingPair(source, 0, '(', ')') === source.length - 1
    ? source.slice(1, -1).trim()
    : null
}

// Read one logical operator token at the current scan position.
function readLogicalOperatorAt(source: string, index: number): LogicalOperator | null {
  if (source[index] === '&' && source[index + 1] === '&') return '&&'
  if (source[index] === '|' && source[index + 1] === '|') return '||'
  return null
}

// Read one comparison operator token at the current scan position.
function readComparisonOperatorAt(source: string, index: number): ComparisonOperator | null {
  const current = source[index]
  const next = source[index + 1] ?? ''
  const afterNext = source[index + 2] ?? ''
  if (current === '=' && next === '=' && afterNext === '=') return '==='
  if (current === '!' && next === '=' && afterNext === '=') return '!=='
  if (current === '>' && next === '=') return '>='
  if (current === '<' && next === '=') return '<='
  if (current === '>') return '>'
  return current === '<' ? '<' : null
}
